import { Badge, Button, Card, CardHeader, Drawer, Table, formatDate, useToast, type Column } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchMonth, markPaid, type PayslipLine } from "../../api/payroll";
import { useServices } from "../../app/services";
import { nameMap, useMyPayslips, usePayslip, useProfiles } from "./hooks";
import { Empty, Field, Gate, PageHead, TableSkeleton, css as s, errorText, isDate, monthLabel, useIntentKey, useScope } from "./kit";

/** `/payroll/payslips/:id` (payroll:view) and `/me/payslips/:id` (own, approved or locked only). */
export function PayslipPage({ own = false }: { own?: boolean }) {
  const { id = "" } = useParams();
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const q = usePayslip(sc, id, own);
  const profiles = useProfiles(sc);
  const p = q.data?.payslip;
  // The payslip does not carry its month's status; mark-paid needs the month approved or locked.
  const month = useQuery({ queryKey: ["payroll", "month", p?.month, p?.branchId, ...sc.scopeKey], queryFn: () => fetchMonth(client, p!.month, p!.branchId), enabled: !own && !!p, retry: false });
  const [open, setOpen] = useState(false);
  const [paidOn, setPaidOn] = useState(sc.today);
  const [paidRef, setPaidRef] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const intent = useIntentKey();

  const pay = useMutation({
    mutationFn: () => markPaid(client, id, { paidOn, paidRef: paidRef.trim() }, intent.key()),
    onSuccess: async () => { intent.renew(); setOpen(false); setErr(null); toast.show("Marked as paid"); await qc.invalidateQueries({ queryKey: ["payroll"] }); },
    onError: (e) => setErr(errorText(e)),
  });
  const refErr = paidRef.trim() === "" ? "Enter the reference you paid with (UTR, cheque number or “cash”)." : null;
  const dateErr = isDate(paidOn) ? null : "Use a date like 2026-11-30.";

  const cols: Array<Column<PayslipLine>> = [
    { key: "label", header: "Line", cell: (l) => <span className={s.truncate} title={l.label}>{l.label}</span> },
    { key: "amount", header: "Amount", align: "end", cell: (l) => <span className={s.num}>{sc.money(l.amount)}</span> },
  ];
  const canMark = !own && !!p && p.paidOn === null && sc.ctx.has("payroll.mark_paid") && (month.data?.month.status === "approved" || month.data?.month.status === "locked");
  const who = p ? nameMap(profiles.data?.profiles).get(p.staffId) : undefined;

  return (
    <div className={s.page}>
      <PageHead title={p ? `Payslip · ${monthLabel(p.month, sc.locale)}` : "Payslip"} sub={who ?? undefined}>
        <Button as={Link} to={own ? "/me/payslips" : p ? `/payroll/${p.month}` : "/payroll"} size="sm">Back</Button>
        {canMark ? <Button variant="primary" size="sm" onClick={() => setOpen(true)}>Mark paid</Button> : null}
      </PageHead>
      <Card aria-label="Payslip">
        <Gate query={q} what="this payslip" skeleton={<TableSkeleton rows={6} label="Loading payslip" />}>
          {({ payslip }) => (
            <>
              <CardHeader title="Summary" subtitle="Paid outside Helmr; this only records that it happened." action={payslip.paidOn ? <Badge tone="ok" dot>Paid {formatDate(payslip.paidOn, sc.locale, "short")}{payslip.paidRef ? ` · ${payslip.paidRef}` : ""}</Badge> : <Badge>Unpaid</Badge>} />
              <div className={s.strip}>
                <div><small>Gross</small><b>{sc.money(payslip.gross)}</b></div>
                <div><small>Deductions</small><b>{sc.money(payslip.deductions)}</b></div>
                <div><small>Net pay</small><b>{sc.money(payslip.net)}</b></div>
              </div>
              {payslip.lines.length === 0 ? <Empty icon="receipt" title="No lines on this payslip" /> : <Table label="Payslip lines" columns={cols} rows={payslip.lines} rowKey={(l) => String(l.seq)} />}
            </>
          )}
        </Gate>
      </Card>

      <Drawer open={open} onOpenChange={setOpen} title="Mark payslip paid" description="Record that you paid this salary outside Helmr. Nothing is sent or moved."
        footer={<><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="primary" disabled={!!refErr || !!dateErr} busy={pay.isPending} busyLabel="Saving…" onClick={() => pay.mutate()}>Mark paid</Button></>}>
        <div className={`${s.rows} ${s.drawerPad}`}>
          <Field label="Paid on" error={dateErr}>{(a) => <input {...a} className={s.input} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
          <Field label="Reference" error={paidRef === "" ? null : refErr} hint="UTR, cheque number or “cash”">{(a) => <input {...a} className={s.input} value={paidRef} maxLength={120} onChange={(e) => setPaidRef(e.target.value)} />}</Field>
          {err ? <p className={s.formErr} role="alert">{err}</p> : null}
        </div>
      </Drawer>
    </div>
  );
}

/** `/me/payslips`: only the caller's own approved or locked payslips (the API filters; nothing here widens it). */
export function MyPayslipsPage() {
  const sc = useScope();
  const q = useMyPayslips(sc);
  return (
    <div className={s.page}>
      <PageHead title="My payslips" sub="Approved and locked months only." />
      <Card aria-label="My payslips">
        <Gate query={q} what="your payslips">
          {(d) => d.payslips.length === 0 ? <Empty icon="receipt" title="No payslips yet" description="Your payslips appear here once the owner approves a month." /> : (
            <Table label="My payslips" columns={[
              { key: "m", header: "Month", cell: (p) => <Link to={`/me/payslips/${p.id}`}>{monthLabel(p.month, sc.locale)}</Link> },
              { key: "net", header: "Net", align: "end", cell: (p) => <b className={s.num}>{sc.money(p.net)}</b> },
              { key: "paid", header: "Paid", cell: (p) => (p.paidOn ? <Badge tone="ok" dot>{formatDate(p.paidOn, sc.locale, "short")}</Badge> : <Badge>Unpaid</Badge>) },
            ]} rows={d.payslips} rowKey={(p) => p.id} />
          )}
        </Gate>
      </Card>
    </div>
  );
}
